// src/components/pages/auth/SignInForm.js
// For new user
// src/components/pages/auth/SignupForm.js
// for login with email and password

import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Input from "../../form/input";
import PasswordInput from "../../form/passwordInput";
import Btn from "../../form/btn";

const API_URL = "https://urchin-app-vijhz-lvk4i.ondigitalocean.app/api/v1";

function validateName(name) {
  return /^[a-zA-Z]+$/.test(name);
}

function validateUsername(userName) {
  return /^[a-zA-Z0-9]+$/.test(userName);
}

function validateEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function validatePassword(password) {
  return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/.test(
    password
  );
}

export default function SignInForm() {
  const { t } = useTranslation();
  const [isChecked, setIsChecked] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    userName: "",
    email: "",
    password: "",
    confirmPassword: "",
  });
  const [errors, setErrors] = useState({});
  const navigate = useNavigate();

  // on Submit
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Prevent multiple submissions
    if (isLoading) return;
    
    const errors = {};

    if (!validateName(formData.firstName)) {
      errors.firstName = t("errors.invalidFirstName");
    }
    if (!validateName(formData.lastName)) {
      errors.lastName = t("errors.invalidLastName");
    }
    if (!validateUsername(formData.userName)) {
      errors.userName = t("errors.invalidUsername");
    }
    if (!validateEmail(formData.email)) {
      errors.email = t("errors.invalidEmail");
    }

    if (!validatePassword(formData.password)) {
      errors.password = t("errors.invalidPassword");
    }
    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = t("errors.passwordsNotMatch");
    }
    if (!isChecked) {
      errors.checkbox = t("errors.termsRequired");
    }

    if (Object.keys(errors).length > 0) {
      setErrors(errors);
      return;
    }

    // send the data to the server
    const data = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      userName: formData.userName,
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.confirmPassword,
    };
    
    // Reset errors and set loading state
    setErrors({});
    setIsLoading(true);

    // send the data to the server
    fetch(`${API_URL}/auth/signup`, {
      method: "POST",
      body: JSON.stringify(data),
      headers: {
        "Content-Type": "application/json",
      },
    })
      .then((res) => {
        // Check if the response is JSON
        const contentType = res.headers.get("content-type");
        if (contentType && contentType.includes("application/json")) {
          return res.json();
        } else {
          throw new Error("Server returned non-JSON response");
        }
      })
      .then((data) => {
        if (data.errors) {
          // Initialize with any existing errors
          setErrors((prev) => {
            const newErrors = { ...prev };

            if (data.errors.includes("Username already in use")) {
              newErrors.userName = t("errors.usernameAlreadyExists");
            }
            if (data.errors.includes("Email already in use")) {
              newErrors.email = t("errors.emailAlreadyExists");
            }

            return newErrors;
          });
        } else {
          // Clear form on success
          setFormData({
            firstName: "",
            lastName: "",
            userName: "",
            email: "",
            password: "",
            confirmPassword: "",
          });
          setIsChecked(false);

          // Redirect on success to the OTP page with the email in body
          navigate("/signup/otp", {
            state: {
              firstName: formData.firstName,
              lastName: formData.lastName,
              userName: formData.userName,
              password: formData.password,
              confirmPassword: formData.confirmPassword,
              email: formData.email,
            },
          });
        }
      })
      .catch((error) => {
        console.error("Error during signup:", error);
        setErrors({ general: t("errors.somethingWentWrong") });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  return (
    <form onSubmit={handleSubmit}>
      {errors && errors.general && (
        <div
          className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4"
          role="alert"
        >
          <span className="block sm:inline">{errors.general}</span>
        </div>
      )}

      <Input
        type="text"
        placeholder="Ex : Gamie"
        label={t("auth.firstName")}
        id="firstname"
        value={formData.firstName}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            firstName: e.target.value,
          }));
        }}
        required
        error={errors.firstName}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 448 512"
          className="w-5 h-5"
        >
          <path d="M304 128a80 80 0 1 0 -160 0 80 80 0 1 0 160 0zM96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM49.3 464l349.5 0c-8.9-63.3-63.3-112-129-112l-91.4 0c-65.7 0-120.1 48.7-129 112zM0 482.3C0 383.8 79.8 304 178.3 304l91.4 0C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7L29.7 512C13.3 512 0 498.7 0 482.3z" />
        </svg>
      </Input>
      <Input
        type="text"
        placeholder="Ex : Surname"
        label={t("auth.lastName")}
        id="lastname"
        value={formData.lastName}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            lastName: e.target.value,
          }));
        }}
        required
        error={errors.lastName}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 448 512"
          className="w-5 h-5"
        >
          <path d="M304 128a80 80 0 1 0 -160 0 80 80 0 1 0 160 0zM96 128a128 128 0 1 1 256 0A128 128 0 1 1 96 128zM49.3 464l349.5 0c-8.9-63.3-63.3-112-129-112l-91.4 0c-65.7 0-120.1 48.7-129 112zM0 482.3C0 383.8 79.8 304 178.3 304l91.4 0C368.2 304 448 383.8 448 482.3c0 16.4-13.3 29.7-29.7 29.7L29.7 512C13.3 512 0 498.7 0 482.3z" />
        </svg>
      </Input>
      <Input
        type="text"
        placeholder="Ex : username123"
        label={t("auth.userName")}
        id="username"
        value={formData.userName}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            userName: e.target.value,
          }));
        }}
        required
        error={errors.userName}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 448 512"
          className="w-5 h-5"
        >
          <path d="M224 256A128 128 0 1 0 224 0a128 128 0 1 0 0 256zm-45.7 48C79.8 304 0 383.8 0 482.3C0 498.7 13.3 512 29.7 512l388.6 0c16.4 0 29.7-13.3 29.7-29.7C448 383.8 368.2 304 269.7 304l-91.4 0z" />
        </svg>
      </Input>
      <Input
        type="email"
        placeholder="Ex : example@gmail.com"
        label={t("auth.email")}
        id="email"
        value={formData.email}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            email: e.target.value,
          }));
        }}
        required
        error={errors.email}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 512 512"
          className="w-5 h-5"
        >
          <path d="M48 64C21.5 64 0 85.5 0 112c0 15.1 7.1 29.3 19.2 38.4L236.8 313.6c11.4 8.5 27 8.5 38.4 0L492.8 150.4c12.1-9.1 19.2-23.3 19.2-38.4c0-26.5-21.5-48-48-48L48 64zM0 176L0 384c0 35.3 28.7 64 64 64l384 0c35.3 0 64-28.7 64-64l0-208L294.4 339.2c-22.8 17.1-54 17.1-76.8 0L0 176z" />
        </svg>
      </Input>

      <PasswordInput
        placeholder="Enter password"
        label={t("auth.password")}
        id="password"
        value={formData.password}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            password: e.target.value,
          }));
        }}
        required
        error={errors.password}
      />

      <PasswordInput
        placeholder="Confirm password"
        label={t("auth.confirmPassword")}
        id="confirmPassword"
        value={formData.confirmPassword}
        onChange={(e) => {
          setFormData((prev) => ({
            ...prev,
            confirmPassword: e.target.value,
          }));
        }}
        required
        error={errors.confirmPassword}
      />

      {/* Checkbox for terms and conditions */}
      <div className="flex items-center mb-4">
        <input
          id="checkbox"
          type="checkbox"
          checked={isChecked}
          onChange={(e) => setIsChecked(e.target.checked)}
          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
        />
        <label htmlFor="checkbox" className="ml-2 text-sm font-medium text-gray-900">
          {t("forms.agreeToTerms")}
        </label>
        {errors.checkbox && (
          <div className="text-red-500 text-sm mt-1">{errors.checkbox}</div>
        )}
      </div>

      <Btn type="submit" disabled={!isChecked || isLoading}>
        {isLoading ? (
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent mr-2"></div>
            {t("auth.creatingAccount")}
          </div>
        ) : (
          t("auth.createAccount")
        )}
      </Btn>
    </form>
  );
}
